<script>
import { mapActions } from "vuex";
import objectMixin from "@/mixins/objectMixin";
import numberMixin from "@/mixins/numberMixin";
import Currency from "@/components/_Shared/Forms/Currency";
import GoogleForms from "@/utils/googleForms.js";

export default {
  namespaced: true,

  components: { Currency },

  mixins: [numberMixin, objectMixin],

  props: {
    withDraw: { type: Boolean, required: true, default: false },
    walletId: { type: String, required: true },
    preloader: { type: Boolean },
    owner: { type: Object, required: true }
  },

  data() {
    return {
      balance: {
        unavailable: null,
        future: null,
        current: null,
        blocked: null,
        available_to_transfer: null
      },
      walletPreloader: false,
      walletError: false,
      toBlockAmount: 0,
      blockAmountPage: false
    };
  },

  methods: {
    ...mapActions("wallets", ["getBalance", "blockAmount"]),
    async updateBalance() {
      this.walletPreloader = true;
      try {
        this.balance = await this.getBalance(this.walletId);
        this.walletPreloader = false;
        this.walletError = false;
      } catch (error) {
        this.walletPreloader = false;
        this.walletError = true;
        throw error;
      }
    },
    async updateBlockedAmount() {
      this.walletPreloader = true;

      let payload = {
        id: this.walletId,
        balance: { blocked: this.toBlockAmount }
      };
      try {
        await this.blockAmount(payload);
        this.blockAmountPage = false;
        await this.updateBalance();
      } catch (error) {
        throw error;
      }
      this.walletPreloader = false;
    },
    openFormPage() {
      const blockAmount = parseFloat(this.toBlockAmount.replace(",", "."));

      if (blockAmount <= 0.0) return;

      window.open(
        GoogleForms.buildGoogleFormsURL("blockedAmountForm", {
          accountable: this.owner,
          amount: blockAmount
        }),
        "_blank"
      );
    }
  },

  async mounted() {
    if (this.walletId) {
      await this.updateBalance();
    }
  },
  watch: {
    async walletId(value) {
      await this.updateBalance(value);
    }
  }
};
</script>

<template>
  <div class="wallet">
    <div class="icon-wallet pointer" @dblclick="updateBalance()">
      <img src="/static/menu-wallet-of-company.svg" />
    </div>

    <v-progress-circular
      v-if="walletPreloader"
      class="preloader"
      :size="80"
      :width="7"
      color="primary"
      indeterminate
    ></v-progress-circular>

    <div v-else-if="walletError" class="content">
      <div class="title-wallet">CARTEIRA INDISPONÍVEL</div>
    </div>

    <div v-else-if="blockAmountPage" class="content">
      <div class="center-button">
        <v-btn
          @click="blockAmountPage = false && updateBalance()"
          outlined
          small
          fab
          color="primary"
        >
          <v-icon>arrow_back</v-icon>
        </v-btn>
      </div>

      <div class="subtitle-wallet with-border">
        <div>QUANTO BLOQUEAR?</div>
      </div>

      <Currency v-model="toBlockAmount" label="" />
    </div>

    <div v-else-if="walletPreloader">
      <v-progress-circular
        :size="80"
        :width="7"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </div>

    <div v-else class="content">
      <div class="balance">
        <div class="coin">R$</div>
        <div class="intval">
          {{ get(balance, "available_to_transfer") | intToDecimal(true) }}
        </div>
        <div class="floatval">
          ,{{
            get(balance, "available_to_transfer") | intToDecimal(false, true)
          }}
        </div>
      </div>

      <div class="title-wallet">SALDO DISPONÍVEL</div>

      <div class="subtitle-wallet with-border">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <div><span v-on="on">SALDO FUTURO</span></div>
          </template>
          <span>Este saldo estará disponível em até 2 dias úteis.</span>
        </v-tooltip>

        <div>{{ get(balance, "future") | intToCurrency }}</div>
      </div>

      <div class="subtitle-wallet with-border">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <div><span v-on="on">SALDO EM CUSTÓDIA</span></div>
          </template>
          <span
            >Este saldo estará disponível as 23h após a realização da limpeza.
          </span>
        </v-tooltip>
        <div>{{ get(balance, "unavailable") | intToCurrency }}</div>
      </div>

      <div class="subtitle-wallet" @click="blockAmountPage = true">
        <v-tooltip right>
          <template v-slot:activator="{ on }">
            <div class="pointer"><u v-on="on">SALDO BLOQUEADO</u></div>
          </template>
          <span>Saldo bloqueado pela Donamaid. Clique para editar.</span>
        </v-tooltip>
        <div>{{ get(balance, "blocked") | intToCurrency }}</div>
      </div>
    </div>

    <v-btn
      v-if="withDraw && !blockAmountPage"
      rounded
      class="button-wallet"
      color="primary"
      @click="$emit('requestWithdraw', balance.available_to_transfer)"
      :loading="preloader"
    >
      sacar
    </v-btn>
    <v-btn
      v-if="blockAmountPage"
      rounded
      class="button-wallet"
      color="primary"
      @click="updateBlockedAmount() && openFormPage()"
    >
      bloquear valor
    </v-btn>
  </div>
</template>

<style scoped>
.preloader {
  min-height: 265px;
}

.pointer {
  cursor: pointer;
  color: #47e8de !important;
}

.center-button {
  display: flex;
  justify-content: center;
  margin: 10px 0px 2px 0px;
}

.wallet {
  padding: 0 50px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid var(--purple);
  border-radius: 8px;
  margin: 20px 25%;
}

.content {
  width: 228px;
  display: flex;
  flex-direction: column;
}

.icon-wallet {
  background-color: #fff;
  width: 80px;
  height: 30px;
  margin-top: -15px;
}

.icon-wallet img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.balance {
  display: flex;
  align-items: flex-end;
  border-bottom: 1px solid var(--purple);
  margin: 10px 0;
  justify-content: center;
}

.balance .coin,
.balance .floatval {
  font-size: 30px;
}

.balance .intval {
  font-size: 80px;
  font-weight: 400;
  margin-bottom: -20px;
  margin-left: 20px;
}

.title-wallet {
  font-size: 25px;
  color: var(--purple);
  font-weight: 600;
  text-align: center;
}

.subtitle-wallet {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 500;
  color: #a6a6a6;
  padding: 8px 0;
}

.subtitle-wallet.with-border {
  border-bottom: 1px solid #d3d3d3;
}

.button-wallet {
  position: relative;
  top: 24px;
  border-radius: 18px;
  min-width: 150px;
  font-size: 24px;
  min-height: 50px;
}

@media (max-width: 480px) {
  .wallet {
    margin: 20px 0%;
  }

  .balance {
    padding: 0;
  }

  .content {
    width: 140px;
  }

  .balance .coin,
  .balance .floatval {
    font-size: 20px;
  }

  .balance .intval {
    font-size: 50px;
    font-weight: 400;
    margin-bottom: -15px;
    margin-left: 4px;
  }

  .subtitle-wallet {
    font-size: 10px;
  }

  .button-wallet {
    min-width: 125px;
    font-size: 20px;
    min-height: 40px;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/_Shared/Wallet.vue